/**
 * ColorPickerEditor
 *
 * Property editor for color-picker control configuration
 * Uses COMPOSITE PATTERN: swatch + hex code + alpha slider
 *
 * Features:
 * - Color swatch preview
 * - Hex code input
 * - Alpha/opacity slider (0-100)
 * - Preset colors
 * - Accessibility: keyboard navigation, ARIA labels
 *
 * Libraries:
 * - No external color library needed (native HTML5 input[type="color"])
 * - Custom alpha slider using standard input[type="range"]
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';

interface ColorPickerConfig {
  defaultColor?: string;
  showAlpha?: boolean;
  defaultAlpha?: number;
  presets?: string[];
  format?: 'hex' | 'rgb' | 'hsla';
}

interface ColorState {
  hex: string;
  alpha: number;
}

/**
 * Composite component: Color Swatch Editor
 * Combines color picker + hex input + alpha slider
 */
function ColorSwatchEditor({
  color,
  onChange,
  onAlphaChange,
  showAlpha,
  alpha,
  presets = [],
}: {
  color: string;
  onChange: (hex: string) => void;
  onAlphaChange?: (alpha: number) => void;
  showAlpha?: boolean;
  alpha: number;
  presets?: string[];
}) {
  const handleColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.currentTarget.value);
    },
    [onChange],
  );

  const handleHexChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let hex = e.currentTarget.value.trim();
      if (!hex.startsWith('#')) hex = '#' + hex;
      if (/^#[0-9A-Fa-f]{6}$/.test(hex)) {
        onChange(hex);
      }
    },
    [onChange],
  );

  const handleAlphaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.currentTarget.value);
      if (onAlphaChange) {
        onAlphaChange(val);
      }
    },
    [onAlphaChange],
  );

  return (
    <div className="af-property-editor__color-swatch-group">
      <div className="af-property-editor__color-row">
        <label htmlFor="color-picker-input" className="af-property-editor__label">
          Color
        </label>
        <input
          id="color-picker-input"
          type="color"
          value={color}
          onChange={handleColorChange}
          className="af-property-editor__color-picker"
          aria-label="Color picker"
        />
        <div
          className="af-property-editor__color-preview"
          style={{
            backgroundColor: color,
            opacity: showAlpha ? alpha / 100 : 1,
          }}
          role="img"
          aria-label={`Color preview: ${color}, alpha: ${alpha}%`}
        />
      </div>

      <div className="af-property-editor__hex-input-group">
        <label htmlFor="hex-input" className="af-property-editor__label">
          Hex Code
        </label>
        <input
          id="hex-input"
          type="text"
          value={color}
          onChange={handleHexChange}
          placeholder="#000000"
          className="af-property-editor__input af-property-editor__input--hex"
          maxLength={7}
          aria-label="Hex color code"
        />
      </div>

      {showAlpha && (
        <div className="af-property-editor__alpha-slider-group">
          <label htmlFor="alpha-slider" className="af-property-editor__label">
            Opacity: {alpha}%
          </label>
          <input
            id="alpha-slider"
            type="range"
            min="0"
            max="100"
            value={alpha}
            onChange={handleAlphaChange}
            className="af-property-editor__range-slider"
            aria-label="Opacity slider"
          />
        </div>
      )}

      {presets.length > 0 && (
        <div className="af-property-editor__preset-colors">
          <p className="af-property-editor__label">Presets</p>
          <div className="af-property-editor__preset-list">
            {presets.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => onChange(preset)}
                className="af-property-editor__preset-button"
                style={{ backgroundColor: preset }}
                title={preset}
                aria-label={`Select color ${preset}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Main ColorPickerEditor render function
 */
function renderColorPickerEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config || {}) as ColorPickerConfig;

  const defaultColor = config.defaultColor || '#000000';
  const showAlpha = config.showAlpha ?? true;
  const defaultAlpha = config.defaultAlpha ?? 100;
  const presets = config.presets || [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
    '#FFFFFF', '#000000', '#808080', '#FFA500',
  ];

  const handleColorChange = useCallback(
    (hex: string) => {
      onConfigChange('defaultColor', hex);
    },
    [onConfigChange],
  );

  const handleAlphaChange = useCallback(
    (alpha: number) => {
      onConfigChange('defaultAlpha', alpha);
    },
    [onConfigChange],
  );

  const handleShowAlpha = useCallback(
    (checked: boolean) => {
      onConfigChange('showAlpha', checked);
    },
    [onConfigChange],
  );

  const handlePresetChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      try {
        const presets = e.currentTarget.value
          .split('\n')
          .map((p) => p.trim())
          .filter((p) => p && /^#[0-9A-Fa-f]{6}$/.test(p));
        onConfigChange('presets', presets);
      } catch {
        // Invalid format, ignore
      }
    },
    [onConfigChange],
  );

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">Color Picker Configuration</h4>

      <div className="af-properties-tab__group">
        <ColorSwatchEditor
          color={defaultColor}
          onChange={handleColorChange}
          onAlphaChange={handleAlphaChange}
          showAlpha={showAlpha}
          alpha={defaultAlpha}
          presets={presets}
        />
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__checkbox-label">
          <input
            type="checkbox"
            checked={showAlpha}
            onChange={(e) => handleShowAlpha(e.target.checked)}
          />
          Enable Alpha/Opacity Slider
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="format-select" className="af-properties-tab__label">
          Color Format
        </label>
        <select
          id="format-select"
          value={(config.format as string) || 'hex'}
          onChange={(e) => onConfigChange('format', e.target.value)}
          className="af-properties-tab__select"
        >
          <option value="hex">Hex (#RRGGBB)</option>
          <option value="rgb">RGB (rgb(r, g, b))</option>
          <option value="hsla">HSLA (hsla(h, s, l, a))</option>
        </select>
      </div>

      <div className="af-properties-tab__group">
        <label htmlFor="presets-textarea" className="af-properties-tab__label">
          Color Presets (one per line)
        </label>
        <textarea
          id="presets-textarea"
          value={presets.join('\n')}
          onChange={handlePresetChange}
          placeholder="#FF0000&#10;#00FF00&#10;#0000FF"
          rows={4}
          className="af-properties-tab__textarea"
          aria-label="Color presets"
        />
      </div>
    </div>
  );
}

/**
 * ColorPickerEditor definition
 */
export const ColorPickerEditor: PropertyEditor = {
  type: 'color-picker',
  label: 'Color Picker',
  description: 'Configure color picker with swatch, hex input, and alpha slider',
  render: renderColorPickerEditor,
};
